import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { X, Send, Loader2, Maximize2, Minimize2, Mic } from "lucide-react";
import { chatbotService } from "../../services/chatbotService";
import { motion, AnimatePresence } from "framer-motion";
import zynAvatar from "../../assets/images/ZYN_1.png";

// --------------------------------------------------------------------------
// Fix inline code fences — AI sometimes outputs text:```json{..} on one line.
// ReactMarkdown requires fences to be on their own lines to parse them.
// --------------------------------------------------------------------------
const preprocessMarkdown = (text) => {
  if (!text) return text;
  let result = text;
  // 1. Ensure opening ``` is preceded by a newline
  result = result.replace(/([^\n])(```)/g, '$1\n$2');
  // 2. Ensure content after ```lang starts on a new line
  result = result.replace(/(```[a-zA-Z0-9_-]*)([ \t]+)(\S)/g, '$1\n$3');
  // 3. Ensure closing ``` is preceded by a newline
  result = result.replace(/(\S)(\n?```(\s|$))/g, '$1\n```');
  return result;
};

// --------------------------------------------------------------------------
// Custom renderers for ReactMarkdown — fixes JSON/code block display
// --------------------------------------------------------------------------
const markdownComponents = (navigate) => ({
  // Handle links specifically to keep them internal SPA links
  a({ node, href, children, ...props }) {
    if (!href) return <a {...props}>{children}</a>;

    let isInternal = false;
    let path = href;

    try {
      // Test if it's an absolute URL
      const url = new URL(href);
      if (
        url.origin === window.location.origin || 
        url.hostname === 'localhost' || 
        url.hostname.includes('zyntegrate')
      ) {
        isInternal = true;
        path = url.pathname + url.search + url.hash;
      }
    } catch (e) {
      // If new URL() throws, it's a relative URL (e.g. "/integrations/123" or "integrations/123")
      if (!href.startsWith('http://') && !href.startsWith('https://') && !href.startsWith('mailto:')) {
        isInternal = true;
        path = href.startsWith('/') ? href : '/' + href;
      }
    }

    if (isInternal) {
      // Clean trailing slashes
      if (path.endsWith('/')) {
        path = path.slice(0, -1);
      }
      
      // AI sometimes returns just `/integrations/[id]` without the suffix.
      // Our React Router requires `/integrations/:id/overview` or `/integrations/:id/details`.
      const parts = path.split('/').filter(Boolean);
      if (parts.length === 2 && parts[0] === 'integrations') {
        path = `${path}/details`;
      }

      return (
        <a
          href={path}
          onClick={(e) => {
            e.preventDefault();
            navigate(path);
          }}
          className="text-indigo-600 hover:text-indigo-800 underline font-sans text-[14px] font-medium cursor-pointer"
        >
          {children}
        </a>
      );
    }
    
    // External links
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-indigo-600 hover:text-indigo-800 underline font-sans text-[14px] font-medium"
        {...props}
      >
        {children}
      </a>
    );
  },
  // Code block: ```json ... ```
  code({ node, inline, className, children, ...props }) {
    const language = className ? className.replace("language-", "") : "";
    const codeText = String(children).replace(/\n$/, "");

    if (inline) {
      return (
        <code
          style={{
            backgroundColor: "#eef2ff",
            color: "#4338ca",
            padding: "1px 5px",
            borderRadius: "4px",
            fontSize: "0.85em",
            fontFamily: "inherit",
            wordBreak: "break-all",
          }}
          {...props}
        >
          {children}
        </code>
      );
    }

    return (
      <div style={{ position: "relative", margin: "8px 0" }}>
        {language && (
          <div
            style={{
              backgroundColor: "#374151",
              color: "#9ca3af",
              fontSize: "0.7em",
              padding: "2px 10px",
              borderRadius: "6px 6px 0 0",
              fontFamily: "monospace",
              userSelect: "none",
            }}
          >
            {language}
          </div>
        )}
        <pre
          style={{
            backgroundColor: "#1f2937",
            color: "#f9fafb",
            padding: "12px",
            borderRadius: language ? "0 0 6px 6px" : "6px",
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "240px",
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: "0.78em",
            fontFamily: "monospace",
            lineHeight: 1.5,
          }}
        >
          <code>{codeText}</code>
        </pre>
      </div>
    );
  },

  // Paragraphs — ensure word wrap
  p({ children }) {
    return (
      <p className="mb-2 font-sans text-[14px] text-gray-900 font-normal leading-relaxed" style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
        {children}
      </p>
    );
  },

  // Bullet lists
  ul({ children }) {
    return (
      <ul className="flex flex-col gap-1.5 my-2 pl-5 list-disc font-sans text-[14px] text-gray-900 font-normal">
        {children}
      </ul>
    );
  },
  ol({ children }) {
    return (
      <ol className="flex flex-col gap-1.5 my-2 pl-5 list-decimal font-sans text-[14px] text-gray-900 font-normal">
        {children}
      </ol>
    );
  },
  li({ children }) {
    return (
      <li className="pl-1 mb-1 font-sans text-[14px] text-gray-900 font-normal" style={{ margin: "3px 0", wordBreak: "break-word", display: "list-item" }}>
        {children}
      </li>
    );
  },

  // Bold
  strong({ children }) {
    return <strong style={{ fontWeight: 600 }}>{children}</strong>;
  },
});

// --------------------------------------------------------------------------
// Size presets
// --------------------------------------------------------------------------
const SIZES = {
  normal: { width: 380, height: 520 },
  large: { width: 450, height: 600 },
};

// --------------------------------------------------------------------------
// Chatbot Component
// --------------------------------------------------------------------------
const Chatbot = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const vadFrameRef = useRef(null);
  const speechDetectedRef = useRef(false);
  const recognitionRef = useRef(null);
  const clickTimerRef = useRef(null);
  const { width, height } = isExpanded ? SIZES.large : SIZES.normal;
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-expand textarea
  const textareaRef = useRef(null);
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [input]);

  // Load history every time chat opens (always fetch fresh data)
  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadHistory = async () => {
    try {
      const history = await chatbotService.getHistory();
      if (history && history.length > 0) {
        const formatted = history.map((msg, i) => ({
          id: `hist-${i}-${Date.now()}`,
          text: msg.message,
          isUser: msg.is_user,
          sessionId: msg.session_id,
        }));
        setMessages(formatted);
        // Ensure we scroll to the bottom after loading history
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        // Show welcome only if no history
        setMessages([{
          id: "welcome",
          text: "Welcome! Curious what Zyntegrate does? Ask me anything about our platform or how to set up your integrations.",
          isUser: false,
        }]);
      }
    } catch (err) {
      console.error("Failed to load chat history", err);
      setMessages([{
        id: "welcome",
        text: "Welcome! Curious what Zyntegrate does? Ask me anything about our platform or how to set up your integrations.",
        isUser: false,
      }]);
    }
  };

  const handleSend = useCallback(async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMsg = { id: Date.now(), text: input.trim(), isUser: true };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    try {
      const data = await chatbotService.sendMessage(userMsg.text);
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, text: data.response, isUser: false },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "Sorry, I encountered an error. Please try again.",
          isUser: false,
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading]);

  // Handle Enter key (Shift+Enter = new line)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  // Voice recording: uses browser SpeechRecognition for real-time text (near-zero latency)
  // Falls back to MediaRecorder + Whisper for unsupported browsers (Firefox)
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  const stopRecording = () => {
    setIsListening(false);
    // Stop SpeechRecognition if active
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} recognitionRef.current = null; }
    // Stop MediaRecorder fallback if active
    if (vadFrameRef.current) { cancelAnimationFrame(vadFrameRef.current); vadFrameRef.current = null; }
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
    analyserRef.current = null;
    speechDetectedRef.current = false;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  const handleVoiceToggle = async () => {
    if (isListening) {
      stopRecording();
      return;
    }

    // ── Path A: Browser SpeechRecognition (Chrome, Edge, Safari) ──
    // Real-time interim results → text appears as user speaks
    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.lang = "en-US";
        recognition.interimResults = true;
        recognition.continuous = false;     // auto-stops on silence
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
          let transcript = "";
          for (let i = 0; i < event.results.length; i++) {
            transcript += event.results[i][0].transcript;
          }
          setInput(transcript);
        };

        recognition.onend = () => {
          setIsListening(false);
          recognitionRef.current = null;
          setTimeout(() => textareaRef.current?.focus(), 0);
        };

        recognition.onerror = (event) => {
          console.error("SpeechRecognition error:", event.error);
          setIsListening(false);
          recognitionRef.current = null;
        };

        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
      } catch (err) {
        console.error("SpeechRecognition failed:", err);
        setIsListening(false);
      }
      return;
    }

    // ── Path B: Fallback — MediaRecorder + Whisper (Firefox, etc.) ──
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000,
        },
      });

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      speechDetectedRef.current = false;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (audioBlob.size === 0) return;

        try {
          const data = await chatbotService.transcribeAudio(audioBlob);
          const transcript = data.transcript || "";
          setInput(transcript);
          setTimeout(() => textareaRef.current?.focus(), 0);
        } catch {
          // Silent fail — user can type manually
        }
      };

      // Silence detection via Web Audio API
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const SILENCE_THRESHOLD = 15;
      const SILENCE_DURATION = 1200;
      const SPEECH_THRESHOLD = 20;

      const monitorAudio = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const rms = Math.sqrt(dataArray.reduce((sum, v) => sum + v * v, 0) / dataArray.length);

        if (rms > SPEECH_THRESHOLD) {
          speechDetectedRef.current = true;
          if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
        } else if (speechDetectedRef.current && rms < SILENCE_THRESHOLD) {
          if (!silenceTimerRef.current) {
            silenceTimerRef.current = setTimeout(() => {
              stopRecording();
            }, SILENCE_DURATION);
          }
        }

        vadFrameRef.current = requestAnimationFrame(monitorAudio);
      };

      mediaRecorder.start();
      setIsListening(true);
      vadFrameRef.current = requestAnimationFrame(monitorAudio);
    } catch (err) {
      console.error("Microphone access denied:", err);
      setIsListening(false);
    }
  };

  // Auth guard
  const token = localStorage.getItem("accessToken");
  if (!token) return null;

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end pointer-events-none">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="chatwindow"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            style={{ width, height }}
            className="mb-4 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col pointer-events-auto overflow-hidden transition-all duration-300"
          >
            {/* ── Header ── */}
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-3 flex justify-between items-center text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30 shadow-md">
                    <img
                      src={zynAvatar}
                      alt="Zyn"
                      className="w-full h-full object-cover"
                      style={{ transform: "scale(1.4)", transformOrigin: "center" }}
                    />
                  </div>
                  <span
                    className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-indigo-600"
                    style={{ boxShadow: '0 0 6px 2px rgba(74, 222, 128, 0.6)' }}
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-sm leading-tight">Zyn</h3>
                  <p className="text-xs text-indigo-100 leading-tight mt-0.5">
                    Ask me anything about Zyntegrate
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {/* Resize toggle */}
                <button
                  onClick={() => setIsExpanded((v) => !v)}
                  title={isExpanded ? "Minimize" : "Expand"}
                  className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
                >
                  {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                {/* Close */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* ── Messages Area ── */}
            <div
              className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-3 bg-gray-50/50"
              style={{ overflowX: "hidden" }}
            >
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.isUser ? "justify-end" : "justify-start"}`}
                >
                  {/* AI avatar */}
                  {!msg.isUser && (
                    <div className="w-7 h-7 rounded-full mr-2 shrink-0 self-end mb-1 overflow-hidden shadow-sm">
                      <img src={zynAvatar} alt="Zyn" className="w-full h-full object-cover" style={{ transform: "scale(1.4)", transformOrigin: "center" }} />
                    </div>
                  )}

                  {/* Bubble */}
                  <div
                    style={{
                      maxWidth: "85%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      overflow: "hidden",
                    }}
                    className={`p-3 rounded-2xl font-sans text-[14px] font-normal leading-relaxed shadow-sm ${msg.isUser
                      ? "bg-indigo-600 text-white rounded-br-none tracking-wide"
                      : `text-gray-900 border rounded-bl-none ${msg.isError
                        ? "border-red-200 bg-red-50 text-red-600"
                        : "border-gray-100 bg-gray-50/80"
                      }`
                      }`}
                  >
                    {msg.isUser ? (
                      msg._transcribing ? (
                        <span className="flex items-center gap-1 h-5">
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </span>
                      ) : (
                        <span style={{ wordBreak: "break-word" }}>{msg.text}</span>
                      )
                    ) : (
                      <div
                        style={{
                          lineHeight: "1.55",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          minWidth: 0,
                        }}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={markdownComponents(navigate)}
                        >
                          {preprocessMarkdown(msg.text)}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="w-7 h-7 rounded-full mr-2 shrink-0 overflow-hidden shadow-sm">
                    <img src={zynAvatar} alt="Zyn" className="w-full h-full object-cover" style={{ transform: "scale(1.4)", transformOrigin: "center" }} />
                  </div>
                  <div className="bg-white p-3 rounded-2xl rounded-bl-none border border-gray-100 shadow-sm flex items-center gap-1">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* ── Input Area ── */}
            <form
              onSubmit={handleSend}
              className="p-3 bg-white border-t border-gray-100 flex gap-2 shrink-0 items-end"
            >
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isListening ? "Listening..." : "Ask about Zyntegrate..."}
                rows={1}
                disabled={isLoading}
                readOnly={isListening}
                style={{
                  resize: "none",
                  overflowY: "auto",
                  maxHeight: "120px",
                  minHeight: "38px",
                  lineHeight: "1.4",
                }}
                className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm placeholder:text-gray-400"
              />
              {/* Mic button: idle → animated pulse when recording */}
              <button
                type="button"
                onClick={handleVoiceToggle}
                disabled={isLoading}
                className={`relative overflow-hidden p-2.5 rounded-xl transition-all shadow-sm hover:shadow-md active:scale-95 shrink-0 ${
                  isListening
                    ? "bg-blue-100 text-blue-600"
                    : "text-blue-500 hover:bg-blue-50 hover:text-blue-600"
                }`}
                style={isListening ? { animation: 'micGlow 1.5s ease-in-out infinite, micBreathe 1.5s ease-in-out infinite' } : {}}
                title={isListening ? "Stop recording" : "Voice input"}
              >
                {isListening && (
                  <span
                    style={{
                      position: 'absolute', inset: 0, borderRadius: '0.75rem',
                      border: '2px solid #3b82f6',
                      animation: 'micPulseRing 1.5s ease-out infinite',
                      pointerEvents: 'none',
                    }}
                  />
                )}
                <Mic size={18} />
              </button>
              <style>{`
                @keyframes micPulseRing { 0% { transform: scale(1); opacity: 0.5; } 100% { transform: scale(2.2); opacity: 0; } }
                @keyframes micGlow { 0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.5); } 50% { box-shadow: 0 0 12px 4px rgba(59,130,246,0.35); } }
                @keyframes micBreathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.12); } }
              `}</style>
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md active:scale-95 shrink-0"
              >
                {isLoading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Floating Button (hidden when chat is open) ── */}
      {!isOpen && isVisible && (
        <button
          onClick={() => {
            if (clickTimerRef.current) return; // double-click in progress
            clickTimerRef.current = setTimeout(() => {
              clickTimerRef.current = null;
              setIsOpen(true);
            }, 250);
          }}
          onDoubleClick={(e) => {
            e.preventDefault();
            if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
            setIsVisible(false);
          }}
          className="pointer-events-auto rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-110 active:scale-95 flex items-center justify-center p-0 bg-transparent border-0 cursor-pointer overflow-hidden animate-[bounce_3s_infinite]"
          style={{ width: 60, height: 60 }}
          title="Double-click to hide"
        >
          <img
            src={zynAvatar}
            alt="Zyn"
            className="w-full h-full object-cover"
            style={{ transform: "scale(1.4)", transformOrigin: "center" }}
          />
        </button>
      )}
    </div>

      {/* ── Restore icon — flush to right border, bottom ── */}
      {!isOpen && !isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          title="Open chat"
          className="fixed z-50 pointer-events-auto border-0 bg-transparent cursor-pointer p-0 overflow-hidden rounded-full opacity-40 hover:opacity-100 transition-opacity duration-200"
          style={{ width: 36, height: 36, bottom: 24, right: 0 }}
        >
          <img
            src={zynAvatar}
            alt="Zyn"
            className="w-full h-full object-cover"
            style={{ transform: "scale(1.4)", transformOrigin: "center" }}
          />
        </button>
      )}
    </>
  );
};

export default Chatbot;
